/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 */
import React, {useLayoutEffect, useRef, useState} from "react";
import AddAssignedItemsContainer from "./SubComponents/AddAssignedItemsContainer";
import AvailableItemsTable from "./SubComponents/AvailableItemsTable";
import {calculateHeight, filterByNameEmailPhoneNumber, isSmWidth} from "../../Apis/functions";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import SelectedUserEmailPhoneCard from "./SubComponents/SelectedUserEmailPhoneCard";
import CustomSearch from "../SearchSimples/CustomSearch";
import NotInformation from "../NotInformation";
/**
 * Component to manage available and selected items with dynamic endpoints,
 * search, filtering, and selection handling.
 *
 * @param {Object} props - Component properties
 * @param {Function} props.setBodyForExport - Sets the request body used for export functionality
 * @param {Object} props.results - Data source containing available items and pagination info
 * @param {Function} props.setResults - Updates the results state
 * @param {string} props.searchElement - Current search input value for available items
 * @param {Function} props.setSearchElement - Updates the search input value
 * @param {Function} props.setSearch - Triggers advanced search behavior
 * @param {Function} props.setSimpleSearch - Triggers simple search behavior
 * @param {boolean} props.modified - Indicates if the data has been modified
 * @param {Function} props.setModified - Updates the modified state
 * @param {Function} props.getChip - Function to render custom chip content for items
 * @param {Function} props.onAdd - Callback executed when an item is added to the selected list
 * @param {Function} props.onDelete - Callback executed when an item is removed from the selected list
 * @param {Array} props.selected - List of currently selected items
 * @param {Function} props.setSelected - Updates the selected items list
 * @param {ReactNode} props.tableIconTitle - Icon displayed in the available items section header
 * @param {ReactNode} props.selectedSectionIconTitle - Icon displayed in the selected items section header
 * @param {string} props.labelRowsPerPage - Label for rows per page in pagination
 * @param {string} props.tableNotInformationLegend - Message displayed when no available data is present
 * @param {string} props.selectedSectionNotInformationLegend - Message displayed when no selected items exist
 * @param {Function} props.selectedSectionSearchInput - Show/hide table search input in selected section
 * @param {Function} props.tableBuildBody - Function to build the request body for table queries
 * @param {Function} props.tableEndPoint - Endpoint function to fetch available items
 * @param {string} props.keyId - Unique identifier key for each item
 * @param {string} props.viewTypeName - View type identifier for rendering variations
 * @param {Function} props.tableTitle - Show/hide table title
 * @param {Function} props.renderAvailableItem - Function used to render each available item row
 * @param {Function} props.renderSelectedItems - Function used to render all selected items
 * @param {boolean} props.emptyFields - When true, flags the selected section with an error border (empty required selection)
 * @returns {JSX.Element} Component with available and selected items management
 */
const CustomAvailableItems = (props) => {
    const {
        id,
        setBodyForExport,
        results,
        setResults,
        searchElement,
        setSearchElement,
        setSearch,
        setSimpleSearch,
        modified,
        setModified,
        getChip,
        onAdd,
        onDelete,
        selected,
        setSelected,
        tableIconTitle,
        selectedSectionIconTitle,
        labelRowsPerPage,
        tableNotInformationLegend,
        selectedSectionNotInformationLegend,
        selectedSectionSearchInput=true,
        tableTitle=true,
        tableBuildBody,
        tableEndPoint,
        keyId,
        viewTypeName,
        renderAvailableItem,
        renderSelectedItems,
        selectedSectionTitle = "selected_employees",
        bottom = 60,
        searchTitle="search_by_name_email_or_phone",
        titleContainer='available_employees',
        containerClass='',
        emptyFields=false
    } = props;

    const [selectedSearchElement, setSelectedSearchElement] = useState("");
    const {t} = useTranslation();
    const selectedRef = useRef(null);
    const notInformationRef = useRef(null);
    const fixedHeight = useRef(null);

    useLayoutEffect(() => {
        if (isSmWidth()) return;

        if (!fixedHeight.current) {
            const anchor = notInformationRef.current ?? selectedRef.current;
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            if (!rect.width && !rect.height) return;
            fixedHeight.current = `${window.innerHeight - (rect.top + bottom)}px`;
        }

        if (notInformationRef.current) notInformationRef.current.style.height = fixedHeight.current;
        if (selectedRef.current) selectedRef.current.style.height = fixedHeight.current;
    }, [selected]);

    const handleAdd = (item) => {
        if(item?.disabled)
            return;

        const exists = selected.some(
            e => String(e[keyId]) === String(item[keyId])
        );

        if (exists) {
            toast.warn(t("the_user_has_already_been_added"));
        } else {
            setSelected(prev => [...prev, item]);
        }

        if(onAdd) onAdd(item)
    }

    const handleDelete = (item) => {
        setSelected(prev => {
            return prev.filter(
                e => String(e[keyId]) !== String(item[keyId])
            );

        });
        toast.success(t('has_been_deleted_successfully'));
        if(onDelete) onDelete(item)
    }

    const tableRender = () => {
        return <AvailableItemsTable
            setBodyForExport={setBodyForExport}
            results={results}
            setResults={setResults}
            searchElement={searchElement}
            setSearchElement={setSearchElement}
            setSearch={setSearch}
            setSimpleSearch={setSimpleSearch}
            modified={modified}
            setModified={setModified}
            labelRowsPerPage={labelRowsPerPage}
            tableNotInformationLegend={tableNotInformationLegend}
            getChip={getChip}
            onAdd={handleAdd}
            tableBuildBody={tableBuildBody}
            tableEndPoint={tableEndPoint}
            keyId={keyId}
            selected={selected}
            viewTypeName={viewTypeName}
            renderAvailableItem={renderAvailableItem}
            bottom={bottom}
            searchTitle={searchTitle}
        />
    }

    return (
        <div className={"row pb-3"}>
            <div className={`col-sm-12 col-md-8 ${id === null && 'pl-0'}`}>
                { tableTitle ?
                    <AddAssignedItemsContainer
                        title={titleContainer}
                        count={results?.totalElements}
                        icon={tableIconTitle}
                        containerClass={containerClass}
                    >
                        <div className={"mx-3 mb-3"}>
                            {tableRender()}
                        </div>
                    </AddAssignedItemsContainer>
                    :
                    tableRender()
                }
            </div>
            <div className={`col-sm-12 col-md-4 ${id === null && 'pr-0'}`}>
                <AddAssignedItemsContainer
                    title={selectedSectionTitle}
                    count={selected?.length}
                    icon={selectedSectionIconTitle}
                    borderClass={emptyFields ? 'border-full-error-code-600' : 'border-full-shades-200'}
                >
                    {selected?.length > 0
                        ?
                        <div className={"p-3 d-flex flex-column"} ref={selectedRef}>
                            { selectedSectionSearchInput &&
                                <CustomSearch
                                    id={"txt_cai_search"}
                                    label={searchTitle}
                                    value={selectedSearchElement}
                                    setValue={setSelectedSearchElement}
                                    onChange={(e) => setSelectedSearchElement(e.target.value)}
                                    onReset={() => setSelectedSearchElement("")}
                                />
                            }
                            <div className='custom-scrollbar' ref={selectedRef}>
                                {renderSelectedItems ?
                                    renderSelectedItems({ items:selected, keyId, selectedSearchElement, handleDelete }):
                                    filterByNameEmailPhoneNumber(selected, selectedSearchElement)?.map((item, index) => (
                                        <div className={"mb-3"}
                                             key={item[keyId] || index}
                                        >
                                            <SelectedUserEmailPhoneCard
                                                keyId={keyId}
                                                item={{...item, disabled: false}}
                                                onDelete={handleDelete}
                                            />
                                        </div>
                                    ))
                                }
                            </div>

                        </div>
                        :
                        <NotInformation text={t(selectedSectionNotInformationLegend)} containerRef={notInformationRef} card={true} />
                    }
                </AddAssignedItemsContainer>
                {emptyFields &&
                    <span className={`ibl-font-w-700 fs-10px font-color-error-600`}>{t('requiredField')}</span>
                }
            </div>
        </div>
    );

}

export default CustomAvailableItems
